(() => {
  /**
   * Client to communicate with the Custom app
   */
  class ConfiguratorClient {
    constructor(shopifyDomain, appHandle) {
      this.shopifyDomain = shopifyDomain;
      this.endpoint = `/apps/${appHandle}/api/v1/configurators`;
    }

    async loadInfo(code) {
      const url = new URL(
        `https://${this.shopifyDomain}${this.endpoint}/load_info`
      );

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code,
            shopify_domain: this.shopifyDomain,
          }),
        });

        if (response.ok) {
          return await response.json();
        }

        throw new Error("Error from server");
      } catch (error) {
        console.error("Error Loading Data", error);
      }
    }
  }

  if (!customElements.get("product-configurator")) {
    /**
     * @class ProductConfigurator
     * A custom element to manage the product configuration modal (iframe),
     * handle communication with the configurator app, and perform cart actions.
     */
    class ProductConfigurator extends HTMLElement {
      static MessageTopics = {
        CONFIGURATION_SAVED: "configurationsaved",
        ADD_TO_CART: "addtocart",
        CONFIGURATION_SAVED_REMOTELY: "configurationsaved_remotely",
        REQUEST_OFFER_FORM_SENT: "requestoffer_form_sent",
      };

      static Events = {
        OPEN: "CONFIGURATOR::OPEN",
      };

      static OperationTypes = {
        ADD: "add",
        CHANGE: "change",
      };

      constructor() {
        super();

        this.debug = true; // Set only to true for development

        this.config = this.getConfig();
        this.elems = this.getElems();
        this.configuratorClient = new ConfiguratorClient(
          this.config.shopifyDomain,
          this.config.appHandle
        );

        this.currentStatus = null;

        this.handleMessageListener = this.handleMessage.bind(this);
        this.handleOpenConfiguratorListener =
          this.handleOpenConfigurator.bind(this);

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      connectedCallback() {
        window.addEventListener("message", this.handleMessageListener);
        document.documentElement.addEventListener(
          "cart:refresh",
          this.cartRefreshHandler
        );
        this.elems.dialog.addEventListener("close", () => {
          this.elems.dialog.querySelector("iframe")?.remove();
        });
        document.addEventListener(
          ProductConfigurator.Events.OPEN,
          this.handleOpenConfiguratorListener
        );
      }

      disconnectedCallback() {
        window.removeEventListener("message", this.handleMessageListener);
        document.documentElement.removeEventListener(
          "cart:refresh",
          this.cartRefreshHandler
        );
        document.removeEventListener(
          ProductConfigurator.Events.OPEN,
          this.handleOpenConfiguratorListener
        );
      }

      /**
       * Handles the 'CONFIGURATOR::OPEN' event to show the modal and load the iframe.
       * @param {CustomEvent} event - The custom event containing configuration details.
       */
      handleOpenConfigurator(event) {
        const { detail } = event;
        const { type, payload } = detail;

        this.log("Opening Configurator with type:", type);

        let url;
        let endpoint;
        let channel = "_default";
        if (this.config.currency !== "EUR") {
          channel = this.config.currency.toLowerCase();
        }
        const language = this.localeMapping(this.config.locale);
        const defaultSearchParams = {
          _display_net_prices: "1",
          _cta_element: "addToCart",
          _channel: channel,
          _language: language,
          price: payload.grossPrice,
          netprice: payload.price,
        };

        let currentStatus = {
          productHandle: payload.productHandle,
          variantConfig: payload.variantConfig,
          variantsMapping: payload.variantsMapping,
          originalPrice: payload.originalPrice,
          type,
        };

        switch (type) {
          case ProductConfigurator.OperationTypes.CHANGE:
            const { code } = payload;
            endpoint = `code:${code}`;
            url = this.buildConfiguratorURL(endpoint, {
              ...defaultSearchParams,
              _fromBasket: "1",
            });
            currentStatus = {
              ...currentStatus,
              lineItemKey: payload.lineItemKey,
            };
            break;
          case ProductConfigurator.OperationTypes.ADD:
          default:
            const { identifier } = payload;
            endpoint = `identifier:${identifier}`;
            url = this.buildConfiguratorURL(endpoint, {
              ...defaultSearchParams,
            });
        }

        this.currentStatus = currentStatus;
        this.addConfiguratorIframe(url);
        this.elems.dialog?.showModal();
      }

      /**
       * Handles messages received from the configurator iframe.
       * @param {MessageEvent} event - The message event from the iframe.
       * @returns {Promise<void>}
       */
      async handleMessage(event) {
        const data = event.data;
        const { method, code, save_id: saveId } = data;

        // this.log("Received message", data);

        switch (method) {
          case ProductConfigurator.MessageTopics.CONFIGURATION_SAVED:
            this.log("User saved configuration with code: " + code);
            break;
          case ProductConfigurator.MessageTopics.ADD_TO_CART:
            if (
              this.currentStatus.type === ProductConfigurator.OperationTypes.ADD
            ) {
              this.handleAddToCart(code);
            } else {
              this.handleUpdateCartItem(code);
            }
            break;
          case ProductConfigurator.MessageTopics.CONFIGURATION_SAVED_REMOTELY:
            this.log(
              `Configuration saved remotely with code: ${code} and save_id: ${saveId}`
            );
            break;
          case ProductConfigurator.MessageTopics.REQUEST_OFFER_FORM_SENT:
            this.log("Request offer form sent for code: " + code);
            break;
          default:
            // this.log("Unhandled Message Topic", method);
        }
      }

      /**
       * Orchestrates adding a newly configured product to the cart.
       * @param {string} code - The configuration code from the iframe.
       * @returns {Promise<void>}
       */
      async handleAddToCart(code) {
        this.log("Adding Product To Cart", code);
        try {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const properties = await this.fetchAndParseLineProperties(code);
          const configurationSelections = JSON.parse(
            properties["_configuration_selections"]
          );
          const firstSelectedVariantSKU = Object.keys(
            configurationSelections
          )[0];
          const firstSelectedVariantId = this.findVariantIdFromSKU(
            firstSelectedVariantSKU,
            this.currentStatus.variantsMapping
          );
          const variantProperties = await this.fetchVariantProperties(
            this.currentStatus.productHandle,
            firstSelectedVariantId
          );
          properties["_configuration_original_price"] = this.currentStatus.originalPrice;

          this.closeConfiguratorIframe();

          const response = await this.addToCart(firstSelectedVariantId, {
            ...properties,
            ...variantProperties,
          });

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: firstSelectedVariantId,
            cartData: response,
          });

          if (!cart) {
            window.location = window.routes.cart_url;
            return
          }

          cart.renderContents(response);
        } catch (error) {
          this.log("There was an error adding Product to Cart", error);
        }
      }

      /**
       * Orchestrates updating an existing line item in the cart with a new configuration.
       * @param {string} newCode - The new configuration code from the iframe.
       * @returns {Promise<void>}
       */
      async handleUpdateCartItem(newCode) {
        try {
          const lineItem = await this.findLineItemByKey(
            this.currentStatus.lineItemKey
          );
          const newProperties = await this.fetchAndParseLineProperties(newCode);

          const cartData = await this.changeLineItem(lineItem.key, {
            ...lineItem.properties,
            ...newProperties,
          });

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: lineItem.variant_id,
            cartData,
          });

          this.closeConfiguratorIframe();
        } catch (error) {
          this.log("There was an issue updating the Cart Item", error);
        }
      }

      /**
       * Fetches data and builds the complete line item properties object for a configuration.
       * @param {string} code - The configuration code.
       * @returns {Promise<object>} A promise that resolves to the properties object.
       */
      async fetchAndParseLineProperties(code) {
        this.log("Creating properties object for code:", code);

        const [clientData, productData] = await Promise.all([
          this.configuratorClient.loadInfo(code),
          this.getProductData(this.currentStatus.productHandle),
        ]);

        const properties = {};
        const serverBody = JSON.parse(clientData.body);
        const variantConfig = this.currentStatus.variantConfig;
        const variantDetail = serverBody.customdata;

        properties["_configuration_original_title"] = productData.product.title;
        properties["_configuration_code"] = code;
        properties["_configuration_hash"] = clientData.token;
        properties["_configuration_title"] = "Your Edition:";
        properties["_configuration_positions"] = JSON.stringify(
          serverBody.calculation.positions
        );
        properties["_bundle_id"] = Date.now();

        if (variantDetail) {
          let variantDetailsMapped = {};
          let selectedAmounts = variantDetail?.selectedAmounts;
          let variantTitles = [];

          Object.keys(selectedAmounts).forEach((key) => {
            variantDetailsMapped[key] = {
              amount: selectedAmounts[key],
            };

            const variant = productData.product.variants?.find(
              (item) => item.sku === key
            );
            if (variant) {
              variantDetailsMapped[key].realVariantPrice = variant.price;
              variantTitles.push(
                `${key}:${variant.title}:${selectedAmounts[key]}`
              );
            } else {
              this.log("Variant not found for SKU:", key);
            }
          });

          properties["_configuration_selections"] =
            JSON.stringify(variantDetailsMapped);
          properties["_configuration_variant_titles"] =
            variantTitles.join(":::");

          if (serverBody.calculation.positions) {
            let positionsString = serverBody.calculation.positions
              .filter((item) => item.selected)
              .map((item) => `${item.title}-:-${item.priceNetFormatted}`)
              .join(":::");
            properties["_configuration_positions_frontend"] = positionsString;
          }
        }

        if (variantConfig) {
          for (let key in variantConfig) {
            if (variantConfig[key] !== null) {
              properties[`_${key}`] = variantConfig[key];
            }
          }
        }

        this.log("Properties object created", properties);
        return properties;
      }

      /**
       * Fetches custom properties for a specific product variant using a custom view.
       * @param {string} productHandle - The handle of the product.
       * @param {string} variantId - The unique ID of the product variant.
       * @returns {Promise<object>} A promise that resolves to an object containing the variant's custom properties.
       * @throws {Error} If the network response is not successful.
       */
      async fetchVariantProperties(productHandle, variantId) {
        const url = new URL(`${location.origin}/products/${productHandle}`);
        url.searchParams.append("variant", variantId);
        url.searchParams.append("view", "properties");

        const response = await fetch(url);
        if (!response.ok) throw new Error("Error fetching Variant Properties");

        const data = await response.json();
        return data["variant_properties"];
      }

      /**
       * Fetches the product object from the Shopify Product JSON endpoint.
       * @param {string} productHandle - The handle of the product to fetch.
       * @returns {Promise<object>} A promise that resolves to the product data object.
       * @throws {Error} If the network response is not successful.
       */
      async getProductData(productHandle) {
        this.log("Fetching Product Data for:", productHandle);
        const response = await fetch(`/products/${productHandle}.json`);
        if (!response.ok) throw new Error("Server error fetching product data");

        const data = await response.json();
        this.log("Product Data Fetched:", data);
        return data;
      }

      /**
       * Fetches the entire cart and finds a specific line item by its key.
       * @param {string} key - The unique key of the line item to find.
       * @returns {Promise<object>} A promise that resolves to the found line item object.
       * @throws {Error} If the line item cannot be found.
       */
      async findLineItemByKey(key) {
        this.log("Fetching Line Item with Key:", key);
        const cartResponse = await fetch("/cart.json");
        const cartData = await cartResponse.json();
        const lineItem = cartData.items.find((item) => item.key === key);

        if (!lineItem) throw new Error(`Line Item not found for key ${key}`);

        this.log("Line Item found:", lineItem);
        return lineItem;
      }

      /**
       * Adds a single product variant to the Shopify cart.
       * @param {string} variantId - The unique ID of the product variant to add.
       * @param {object} [properties={}] - Optional custom line item properties.
       * @returns {Promise<void>} A promise that resolves when the item is successfully added.
       * @throws {Error} If the network response is not successful.
       */
      async addToCart(variantId, properties = {}) {
        const formData = new FormData();
        formData.append('quantity', 1);
        formData.append('id', variantId);
        formData.append('form_type', 'product');
        for (const [key, value] of Object.entries(properties)) {
          formData.append(`properties[${key}]`, value);
        }
        
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(`${routes.cart_add_url}`, config);

        if (!response.ok) throw new Error("Error adding Product to the Cart");

        return await response.json();
      }

      /**
       * Sends a request to the Shopify cart to change an existing line item's properties.
       * @param {string} key - The unique key of the line item to change.
       * @param {object} properties - The new set of properties for the line item.
       * @returns {Promise<any>} A promise that resolves when the item is successfully changed.
       * @throws {Error} If the network response is not successful.
       */
      async changeLineItem(key, properties) {
        const response = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: key, properties }),
        });
        if (!response.ok) throw new Error("Error with Change Line item POST");

        return await response.json();
      }

      /**
       * Creates and injects the configurator iframe into the dialog element.
       * @param {URL} url - The URL object for the iframe's src attribute.
       */
      addConfiguratorIframe(url) {
        this.log("Adding Iframe:", url.toString());
        const iframeHTML = `<iframe src="${url.toString()}" id="configurator_page" width="100%" height="100%" allow="clipboard-write self *;" frameborder="0"></iframe>`;
        this.elems.dialog.insertAdjacentHTML("afterbegin", iframeHTML);
      }

      /**
       * Closes the Configurator iframe
       */
      closeConfiguratorIframe() {
        this.elems.dialog.close();
      }

      /**
       * Constructs the full URL for the configurator iframe with search parameters.
       * @param {string} endpoint - The specific endpoint for the configurator.
       * @param {object} [searchParams={}] - An object of search parameters to append.
       * @returns {URL} The complete URL object.
       */
      buildConfiguratorURL(endpoint, searchParams = {}) {
        this.log("Building Configurator URL with:", { endpoint, searchParams });
        const url = new URL(
          `https://${this.config.configuratorUrl}${endpoint}`
        );
        for (const [key, value] of Object.entries(searchParams)) {
          url.searchParams.append(key, value);
        }
        return url;
      }

      /**
       * Finds a variant ID corresponding to a given SKU from a mapping array.
       * @param {string} variantSKU - The SKU to search for.
       * @param {Array<object>} variantsMapping - An array of variant objects, each with 'sku' and 'id'.
       * @returns {string | undefined} The matching variant ID, or undefined if not found.
       */
      findVariantIdFromSKU(variantSKU, variantsMapping) {
        const variant = variantsMapping.find(
          (variant) => variant.sku === variantSKU
        );
        return variant.id;
      }

      /**
       * Maps a Shopify locale string (e.g., 'de') to a different format (e.g., 'de_DE').
       * @param {string} shopifyLocale - The two-letter Shopify locale code.
       * @returns {string} The mapped locale string.
       */
      localeMapping(shopifyLocale) {
        switch (shopifyLocale) {
          case "de":
            return "de_DE";
          case "nl":
            return "nl_NL";
          case "en":
          default:
            return "en_GB";
        }
      }

      /**
       * A logging utility that only outputs to the console if the debug flag is true.
       * @param {...any} args - The values to be logged.
       */
      log(...args) {
        if (this.debug) {
          console.log("[Product Configurator]", ...args);
        }
      }

      /**
       * Gets the component's configuration object by parsing a JSON script tag.
       * @returns {object} The configuration object.
       */
      getConfig() {
        const configEl = this.querySelector("[data-config]");
        if (!configEl) return {};
        try {
          return JSON.parse(configEl.textContent || "{}");
        } catch (error) {
          this.log("Error parsing configuration JSON:", error);
          return {};
        }
      }

      /**
       * Queries the DOM for essential elements and returns them in an object.
       * @returns {{dialog: Element}} An object of DOM elements.
       */
      getElems() {
        return {
          dialog: this.querySelector("[data-product-configurator-dialog]"),
        };
      }
    }

    class ProductConfiguratorButton extends HTMLElement {
      constructor() {
        super();

        this.debug = true;

        this.config = this.getConfig();
        this.elems = this.getElems();

        this.log(
          "Initialising Product Configurator Button with",
          this.config,
          this.elems
        );
        this.init();
      }

      init() {
        this.elems.button.addEventListener("click", () => {
          const { type, code, productHandle, variantsMapping } = this.config;
          const { identifier } = this.dataset;
          const { price, grossPrice, originalPrice } = this.getPrices();
          const currentVariantId = this.getCurrentVariantId();
          const variantConfig = this.config.variantsConfig[currentVariantId];

          const detail = {
            type,
            payload: {
              productHandle,
              price,
              grossPrice,
              originalPrice,
              variantConfig,
              variantsMapping,
              ...(type === ProductConfigurator.OperationTypes.ADD
                ? { identifier }
                : {}),
              ...(type === ProductConfigurator.OperationTypes.CHANGE
                ? { code, lineItemKey: this.dataset.lineItemKey }
                : {}),
            },
          };

          this.log("Sending event with detail", detail);

          const event = new CustomEvent(ProductConfigurator.Events.OPEN, {
            bubbles: true,
            detail,
          });

          this.dispatchEvent(event);
        });
      }

      getCurrentVariantId() {
        return Number(this.dataset.currentVariantId);
      }

      getPrices() {
        return {
          price: this.dataset.price,
          grossPrice: this.dataset.grossPrice,
          originalPrice: this.dataset.originalPrice,
        };
      }

      getConfig() {
        const { type, code, productHandle, price, grossPrice } = this.dataset;
        const variantsConfig = this.querySelector(
          "[data-variant-configs]"
        ).innerText;
        const variantsMapping = this.querySelector(
          "[data-variants-mapping]"
        ).innerText;

        return {
          productHandle,
          type,
          code,
          variantsConfig: JSON.parse(variantsConfig || "null"),
          variantsMapping: JSON.parse(variantsMapping || "null"),
        };
      }

      getElems() {
        return {
          button: this.querySelector("button"),
        };
      }

      log(...args) {
        if (this.debug) {
          console.log("[Product Configurator Button]", ...args);
        }
      }
    }

    customElements.define("product-configurator", ProductConfigurator);
    customElements.define(
      "product-configurator-button",
      ProductConfiguratorButton
    );
  }
})();
